import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import React from 'react';
import { Typeahead } from './Typeahead';
import { fetchForms } from '../../apis';
import { Map } from 'immutable';
import { defineKqlQuery } from '@kineticdata/react';

var fields = function fields(isDatastore) {
  return isDatastore ? [{
    name: 'name'
  }, {
    name: 'slug'
  }] : [{
    name: 'name'
  }, {
    name: 'slug'
  }, {
    name: 'category'
  }];
}; // If dynamic is true, only use operators that can support a single string value


var OPERATORS = function OPERATORS(dynamic) {
  return Map(_objectSpread({
    equals: 'equals',
    matches: 'matches',
    startsWith: 'startsWith'
  }, dynamic ? {} : {
    "in": 'in'
  }));
}; // Dynamically build query using correct operators


var buildQuery = function buildQuery(searchFields, value) {
  // Start building a KQL query using any provided static values
  var fixedQueryBuilder = searchFields // Filter to fields that provided a value
  .filter(function (field) {
    return field.name && field.value;
  }) // For each field, apply the appropriate operator function
  .reduce(function (filter, _ref) {
    var name = _ref.name,
        operator = _ref.operator,
        strict = _ref.strict;
    return filter[OPERATORS(false).get(operator, 'startsWith')](name, name, strict);
  }, defineKqlQuery()); // Add any fields using the typed in dynamic value to the KQL query

  var queryBuilder = searchFields // Filter to fields that didn't provide a value
  .filter(function (field) {
    return field.name && !field.value;
  }) // For each field, apply the appropriate operator function
  .reduce(function (filter, _ref2) {
    var name = _ref2.name,
        operator = _ref2.operator,
        strict = _ref2.strict;
    return filter[OPERATORS(true).get(operator, 'startsWith')](name, name, strict);
  }, // Start with the above KQL query and start an OR block
  fixedQueryBuilder.or()) // End once for the OR block and once to build the query
  .end().end(); // Build values object using by query builder

  var values = searchFields.reduce(function (values, field) {
    return _objectSpread(_objectSpread({}, values), {}, _defineProperty({}, field.name, field.value || value));
  }, {}); // Build the query

  return queryBuilder(values);
};

var searchForms = function searchForms(_ref3) {
  var _ref3$search = _ref3.search,
      search = _ref3$search === void 0 ? Map() : _ref3$search;
  return function (field, value, callback) {
    var searchFields = Map.isMap(search) && search.has('fields') && !search.get('fields').isEmpty() ? search.get('fields').toJS() : fields(search.get('datastore'));
    return fetchForms({
      datastore: search.get('datastore'),
      kappSlug: search.get('kappSlug'),
      q: buildQuery(searchFields, value),
      include: search.get('include') || (search.get('datastore') ? '' : 'categorizations.category'),
      limit: search.get('limit') || 25
    }).then(function (_ref4) {
      var forms = _ref4.forms,
          error = _ref4.error,
          nextPageToken = _ref4.nextPageToken;
      return {
        suggestions: forms || [],
        error: error,
        nextPageToken: nextPageToken
      };
    }).then(callback);
  };
};

var formToValue = function formToValue(form) {
  return form && form.get('slug') || '';
}; // Converts a typed in value to an option object. Used when adding custom values
// when allowNew is true.


var valueToCustomForm = function valueToCustomForm(_ref5) {
  var allowNew = _ref5.allowNew;
  return function (value) {
    return value.length > 0 ? typeof allowNew !== 'function' || allowNew(value) ? {
      slug: value
    } : null : null;
  };
};

var getStatusProps = function getStatusProps(_ref6) {
  var _ref6$search = _ref6.search,
      search = _ref6$search === void 0 ? Map() : _ref6$search,
      _ref6$messages = _ref6.messages;
  _ref6$messages = _ref6$messages === void 0 ? {} : _ref6$messages;

  var _ref6$messages$short = _ref6$messages["short"],
      _short = _ref6$messages$short === void 0 ? 'Type to find a form.' : _ref6$messages$short,
      _ref6$messages$empty = _ref6$messages.empty,
      empty = _ref6$messages$empty === void 0 ? 'No matching forms.' : _ref6$messages$empty,
      _ref6$messages$custom = _ref6$messages.custom,
      custom = _ref6$messages$custom === void 0 ? 'No matching forms. Type to enter a custom option.' : _ref6$messages$custom,
      _ref6$messages$pendin = _ref6$messages.pending,
      pending = _ref6$messages$pendin === void 0 ? 'Searching...' : _ref6$messages$pendin,
      _ref6$messages$more = _ref6$messages.more,
      more = _ref6$messages$more === void 0 ? "Too many forms, first ".concat(search.get('limit') || 25, " shown. Please refine your search.") : _ref6$messages$more,
      _ref6$messages$error = _ref6$messages.error,
      error = _ref6$messages$error === void 0 ? 'There was an error fetching forms.' : _ref6$messages$error;

  return function (props) {
    return {
      info: props["short"] ? _short : props.pending ? pending : null,
      warning: props.error || props.empty || props.more ? props.error ? error : props.more ? more : props.empty ? props.custom ? custom : empty : null : null
    };
  };
};

export var FormSelect = function FormSelect(props) {
  return /*#__PURE__*/React.createElement(Typeahead, {
    components: props.components || {},
    disabled: props.disabled,
    multiple: props.multiple,
    custom: props.allowNew && valueToCustomForm(props),
    search: searchForms(props),
    minSearchLength: props.minSearchLength,
    getSuggestionValue: formToValue,
    getStatusProps: getStatusProps(props),
    value: props.value,
    onChange: props.onChange,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    placeholder: props.placeholder
  });
};